import React from 'react'
import styles from './A23MiniGameTable.module.css';
import { 
  useGameDefinition,
  useIsTourneyGame,
  useTotalPotAmount
} from '@/src/hooks/data/useGameTable';
import { GameTypeTextAbbr, parseDecimalValuesOfNumber } from '../../../../Common/utils/GameUtils';
import { useDispatch } from 'react-redux';
import { updateActiveGame } from '@/Poker/redux/slices/gameTableSlice';
import useActiveGame from '@/src/hooks/data/useActiveGame';
import useClientCards from '@/src/hooks/data/useClientCards';
import { motion } from 'motion/react';
import { ImHeart, ImSpades } from "react-icons/im";
import { GiClubs } from "react-icons/gi";
import { RiPokerDiamondsFill } from "react-icons/ri";
import gameAssets from '@themed-assets';
import useTableSettings from '@/src/hooks/data/useTableSettings';

const SUIT_ICONS = {
    h: ImHeart,
    d: RiPokerDiamondsFill,
    c: GiClubs,
    s: ImSpades,
};

export default function MiniGameTable({ tempTableId }) {
    const dispatch = useDispatch();
    const gameDefinition = useGameDefinition(tempTableId);
    const isTourneyGame = useIsTourneyGame(tempTableId);
    const totalPotAmt = useTotalPotAmount(tempTableId);
    const activeGame = useActiveGame();
    const cards = useClientCards(tempTableId);
    const { tableSettings: { sortCardsByRank } } = useTableSettings();

    const handleGameTableClick = () => {
        dispatch(updateActiveGame(tempTableId));
    }

    // Function to get card display info
    const getCardDisplay = (cardString) => {
        if (!cardString || cardString.length < 2) return null;

        const suit = cardString[0];
        const value = cardString.slice(1);

        const valueMap = {
            '1': 'A',
            '13': 'K',
            '12': 'Q',
            '11': 'J'
        };

        return {
            suit,
            value: valueMap[value] || value,
            numericValue: parseInt(value) || 0
        };
    };

    // Check if user has cards and sort them in ascending order
    const hasCards = cards && cards.length > 0;
    const sortedCards = hasCards ? (sortCardsByRank ? [...cards].sort((a, b) => {
        const cardA = getCardDisplay(a);
        const cardB = getCardDisplay(b);
        if (!cardA || !cardB) return 0;
        
        // Handle Ace (1) as highest value for sorting
        const valueA = cardA.numericValue === 1 ? 14 : cardA.numericValue;
        const valueB = cardB.numericValue === 1 ? 14 : cardB.numericValue;
        
        return valueA - valueB;
    }) : cards) : [];

    return (
        <motion.div
            onClick={handleGameTableClick}
            className={`${styles.miniGameContainer} ${activeGame !== tempTableId ? styles.inactiveGame : ''}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
        >
            {
                hasCards && totalPotAmt > 0
                    ?
                    <div
                        className={styles.potAmount}
                    >
                        <img
                            src={gameAssets.brandChip}
                            alt="Chip"
                            className={styles.chipIcon}
                        />
                        <span className={styles.potValue}>
                            {parseDecimalValuesOfNumber(totalPotAmt, 0)}
                        </span>
                    </div>
                    : null
            }

            {hasCards ? (
                <motion.div
                    className={styles.cardsContainer}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, staggerChildren: 0.1 }}
                >
                    <div className={styles.cardsRow}>
                        {sortedCards.map((card, index) => {
                            const cardInfo = getCardDisplay(card);
                            if (!cardInfo) return null;
                            const Icon = SUIT_ICONS[cardInfo.suit];

                            return (
                                <motion.div
                                    key={`${card}-${index}`}
                                    className={styles.card}
                                    initial={{ opacity: 0, rotateY: -90, x: -20 }}
                                    animate={{ opacity: 1, rotateY: 0, x: 0 }}
                                    transition={{
                                        duration: 0.6,
                                        delay: index * 0.1,
                                        type: "spring",
                                        stiffness: 200
                                    }}
                                    whileHover={{
                                        scale: 1.1,
                                        rotateY: 10,
                                        z: 10
                                    }}
                                >
                                    <div className={styles.cardValue}>
                                        {cardInfo.value}
                                    </div>
                                    <div className={styles.cardSuit}>
                                        {Icon ? <Icon /> : null}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>
            ) : (
                <motion.div
                    className={styles.miniGameTableInfo}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                >
                    <div className={styles.gameTypeText}>
                        {isTourneyGame && <div className={styles.trophyIcon}>🏆</div>}
                        {GameTypeTextAbbr(gameDefinition.gameType)}
                    </div>
                    <div className={styles.gameBlindText}>
                        {gameDefinition.smallBlind}/{gameDefinition.bigBlind}
                    </div>
                </motion.div>
            )}
        </motion.div>
    )
}
